import {memo, useEffect, useMemo, useRef, useState} from "react";

import MusicShelfBox from "../../MusicShelfBox/MusicShelfBox";
import MusicShelfCard from "../../MusicShelfCard/MusicShelfCard";
import ArrowBtn from "../../../UI/ArrowBtn/ArrowBtn";

import {getKey} from "../../../../functions/uniqueSuffix";

import styles from "../MusicShelfList.module.scss";

const chunk = (items, size) => {
  const chunks = [];

  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }

  return chunks;
};

function MusicShelfCarousel({
  list,
  isRectangleBox,
  cardWidth,
  cardGap,
  cardColumns,
  cardRows,
  carousellMargin,
  listHeight,
}) {
  const carouselRef = useRef(null);
  const [carouselColumns, setCarouselColumns] = useState(cardColumns);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const updateColumns = () => {
      const carouselWidth = carouselRef.current?.offsetWidth;

      if (!carouselWidth) return;

      const availableWidth = Math.max(carouselWidth - (carousellMargin || 0), 0);
      const totalCardWidth = Math.max((cardWidth || 210) + (cardGap || 15), 1);
      const nextColumns = Math.max(
        1,
        Math.min(cardColumns || 1, Math.floor(availableWidth / totalCardWidth) || 1)
      );

      setCarouselColumns(nextColumns);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);

    return () => {
      window.removeEventListener("resize", updateColumns);
    };
  }, [cardColumns, cardGap, cardWidth, carousellMargin]);

  const pageSize = Math.max(1, carouselColumns * (cardRows || 1));
  const pages = useMemo(() => chunk(list || [], pageSize), [list, pageSize]);

  useEffect(() => {
    setCurrentPage(prev => Math.min(prev, Math.max(pages.length - 1, 0)));
  }, [pages.length]);

  const canGoLeft = currentPage > 0;
  const canGoRight = currentPage < pages.length - 1;

  return (
    <div
      ref={carouselRef}
      className={styles["music-card-carousel-list"]}
      style={{minHeight: `${listHeight}rem`, overflow: "hidden"}}>
      {canGoLeft && (
        <div
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
          role="button"
          aria-label="Previous items"
          style={{position: "absolute", inset: 0, pointerEvents: "none"}}>
          <div style={{pointerEvents: "auto"}}>
            <ArrowBtn type="left" />
          </div>
        </div>
      )}

      {canGoRight && (
        <div
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, pages.length - 1))}
          role="button"
          aria-label="Next items"
          style={{position: "absolute", inset: 0, pointerEvents: "none"}}>
          <div style={{pointerEvents: "auto"}}>
            <ArrowBtn type="right" />
          </div>
        </div>
      )}

      <div style={{overflow: "hidden", width: "100%"}}>
        <div
          style={{
            display: "flex",
            width: `${pages.length * 100}%`,
            transform: `translateX(-${(100 / Math.max(pages.length, 1)) * currentPage}%)`,
            transition: "transform 250ms ease",
          }}>
          {pages.map((page, pageIndex) => (
            <ul
              key={getKey(`carousel-page-${pageIndex}`)}
              style={{
                width: `${100 / Math.max(pages.length, 1)}%`,
                minWidth: `${100 / Math.max(pages.length, 1)}%`,
                listStyle: "none",
                display: "grid",
                gridTemplateColumns: `repeat(${carouselColumns}, minmax(0, 1fr))`,
                columnGap: `${cardGap || 15}px`,
                rowGap: "1.75rem",
                margin: 0,
                padding: 0,
                alignItems: "start",
              }}>
              {page.map((item, index) => (
                <li
                  key={getKey(
                    `${pageIndex}-${index}-${item?.playlistId || ""}-${item?.videoId || ""}-${item?.browseId || ""}`
                  )}
                  style={{display: "flex", justifyContent: "center"}}>
                  {isRectangleBox ? (
                    <MusicShelfBox data={item} />
                  ) : (
                    <MusicShelfCard data={item} />
                  )}
                </li>
              ))}
            </ul>
          ))}
        </div>
      </div>
    </div>
  );
}

export default memo(MusicShelfCarousel);

